import {
  AudienceComparisonColumn,
  AudienceOptions,
  AudienceSettings as AudienceSettingsData,
} from "../../types/audience-compare";
import {
  parseSearchParams,
  serializeQueryParams,
  useUrlNav,
} from "../../utils/url";

import AudienceCompareResultsLoader from "../../components/audience-compare-results-loader/AudienceCompareResultsLoader";
import AudienceComparisonLabel from "../../components/audience-comparison-label/AudienceComparisonLabel";
import AudienceSettings from "../../components/audience-settings/AudienceSettings";
import { CSOptionItem } from "../../types";
import { CheckboxGroup } from "../../components/forms";
import RemoteData from "../../components/remote-data/RemoteData";
import Tabs from "../../components/tabs/Tabs";
import css from "./AudienceCompare.module.css";
import { envConfig } from "../../config";
import { findAudienceOptionOrFirstEntry } from "../../utils/audience-options";
import { parseColumns } from "../../utils/audience-compare";
import { parseRelativeDate } from "../../utils/date";
import { spaces } from "../../consts/spaces.json";
import { trackPageView } from "../../utils/analytics";
import { useAudienceSettingOptions } from "../../queries";
import { useCurrentUser } from "../../context/auth";
import { useEffect } from "react";

type UrlParams = AudienceSettingsData & {
  tab: string;
  columns: AudienceComparisonColumn[];
};

type AudienceCompareWithDataProps = {
  audienceOptions: AudienceOptions;
  baselineOptions: AudienceOptions;
};

const columnOptions: CSOptionItem<AudienceComparisonColumn>[] = [
  { id: "index", label: "Index" },
  { id: "composition", label: "Composition" },
];

const AudienceCompareWithData = ({
  audienceOptions,
  baselineOptions,
}: AudienceCompareWithDataProps) => {
  const urlNav = useUrlNav();

  const params: UrlParams = parseSearchParams((x) => ({
    tab: x.get("tab") ?? spaces[0].label,
    audience: x.get("audience") ?? envConfig.audienceDefaultId,
    baseline: x.get("baseline") ?? envConfig.baselineDefaultId,
    dateFilter: parseRelativeDate(x.get("dateFilter")) ?? "all",
    columns: parseColumns(x.getAll("columns")),
  }));

  // record a "pageview" each time the tab changes
  useEffect(() => trackPageView(), [params.tab]);

  const selectedAxes = spaces.find((x) => x.label === params.tab)?.axes || [];
  const selectedAudience = findAudienceOptionOrFirstEntry(
    params.audience,
    envConfig.audienceDefaultId,
    audienceOptions,
  );
  const selectedBaseline = findAudienceOptionOrFirstEntry(
    params.baseline,
    envConfig.baselineDefaultId,
    baselineOptions,
  );

  const updateUrl = (nextParams: UrlParams) => {
    urlNav.push(serializeQueryParams(nextParams));
  };

  const handleTabChange = (tab: string) => {
    updateUrl({ ...params, tab });
  };

  const handleSettingsChange = (newSettings: AudienceSettingsData) => {
    updateUrl({ ...params, ...newSettings });
  };

  const handleColumnsChange = (columns: string[]) => {
    updateUrl({ ...params, columns: columns as AudienceComparisonColumn[] });
  };

  return (
    <>
      <AudienceSettings
        audience={selectedAudience.value}
        audienceOptions={audienceOptions}
        baseline={selectedBaseline.value}
        baselineOptions={baselineOptions}
        dateFilter={params.dateFilter}
        onChange={handleSettingsChange}
      />
      <Tabs
        activeTab={params.tab}
        onTabClick={handleTabChange}
        tabs={spaces.map((x) => ({ id: x.label, label: x.label }))}
        className="mt-3"
      />
      <section className={css.results}>
        <div className="d-flex justify-content-between gap-2 mb-2">
          <AudienceComparisonLabel
            audienceWeighting={selectedAudience?.weighting}
            audience={selectedAudience?.label}
            baseline={selectedBaseline?.label}
            dateFilter={params.dateFilter}
          />
          <div>
            <CheckboxGroup
              options={columnOptions}
              onChange={handleColumnsChange}
              selectedOptions={params.columns}
              ariaLabel="Visible columns"
            />
          </div>
        </div>
        <AudienceCompareResultsLoader
          key={Object.values(params).join("-")}
          axes={selectedAxes}
          audienceType={selectedAudience.type}
          audience={selectedAudience.value}
          baseline={selectedBaseline.value}
          baselineWeighting={selectedBaseline.weighting}
          audienceWeighting={selectedAudience.weighting}
          dateFilter={params.dateFilter}
          columns={params.columns as AudienceComparisonColumn[]}
        />
      </section>
    </>
  );
};

/**
 * Renders the audience compare page.
 *
 * This component first fetches data for the settings dropdowns.
 * We do this first because if the values fail to load we can't really display the UI.
 * Fetching subsequent table data depends on IDs from the dropdowns.
 */
const AudienceCompare = () => {
  const user = useCurrentUser();
  const settingOptions = useAudienceSettingOptions(user.accountRefid);

  return (
    <RemoteData
      query={settingOptions}
      whenData={({ data }) => (
        <AudienceCompareWithData
          baselineOptions={data.baselines}
          audienceOptions={data.sources}
        />
      )}
    />
  );
};

export default AudienceCompare;
